import {
  Box,
  Button,
  Flex,
  Link,
  Text,
  VisuallyHidden,
} from "@chakra-ui/react";
import { FieldArray, useFormikContext } from "formik";
import React from "react";
import { RiAddCircleLine } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { SearchSelect } from "src/components/Inputs/SearchSelect";
import { Item } from "src/components/Inputs/SearchSelect/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { WithFeatureFlags } from "src/components/Permissions/WithFeatureFlags";
import { SegmentedControl } from "src/components/SegmentedControl";
import { HighlightedText } from "src/components/Text/HighlightedText";
import { isNonEmptyString } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";
import { useLiveEditConfirmation } from "../LiveEditConfirmation";
import { SchoolItem } from "../QuestionForm/types";
import {
  findRelatedQuestionVisibilityByAnswerId,
  FormikType,
  INVALID_ANSWERS,
  INVALID_SCHOOL_SELECTION_KEY,
  NewRule,
  RuleSchema,
  SchoolSelectionItem,
  toAnswerOptions,
  toQuestionItems,
} from "./helpers";

type Props = {
  sectionType: AF.V3.SectionType;
  otherQuestions: readonly AF.V3.Question[];
  schools: RD.RemoteData<unknown, SchoolItem[]>;
};
export const QuestionVisibility: React.FC<Props> = ({
  sectionType,
  otherQuestions,
  schools: schoolsRD,
}) => {
  const formik = useFormikContext<FormikType>();
  const formikValue = formik.values.questionVisibility;

  const LiveEditConfirmation = useLiveEditConfirmation();

  if (formikValue.switch !== "checked") {
    return null;
  }

  const lastRule = formikValue.rules[formikValue.rules.length - 1];
  const canAddQuestion = RuleSchema.safeParse(lastRule).success;
  return (
    <Flex direction="column" gap="6">
      <FieldArray name="questionVisibility.rules">
        {(arrayHelpers) => {
          return (
            <Flex direction="column" gap="4">
              <Flex direction="column" gap="2">
                <Text fontSize="sm" fontWeight="medium">
                  Conditional reveal
                </Text>
                <Text fontSize="sm" fontWeight="normal" color="gray.600">
                  {sectionType === "PreRankingSection"
                    ? `Choose a question and at least one answer that will make this
                    question appear when families fill out the form. You can pick
                    any single select or multi select question that has keys.`
                    : `Choose a question and at least one answer that will make this
                    question appear when families fill out the form. You can pick
                    any single select or multi select question that has keys. You
                    can also make this question show up based on which school they
                    pick by looking for "School selection" below.`}
                </Text>
                {formikValue.rules.map((rule, ruleIndex) => {
                  const isLastRule = ruleIndex === formikValue.rules.length - 1;
                  return (
                    <Flex direction="column" key={ruleIndex} gap="4">
                      <RemoteDataView
                        remoteData={schoolsRD}
                        error={() => <GenericError />}
                      >
                        {(schools) => (
                          <>
                            <Rule
                              sectionType={sectionType}
                              otherQuestions={otherQuestions}
                              schools={schools}
                              ruleIndex={ruleIndex}
                              onQuestionKeyChange={async (
                                key: string | undefined
                              ) => {
                                if (
                                  !(await LiveEditConfirmation.confirmQuestionVisibility())
                                ) {
                                  return;
                                }
                                await formik.setFieldValue(
                                  `questionVisibility.rules[${ruleIndex}].questionKey`,
                                  key
                                );
                                await formik.setFieldValue(
                                  `questionVisibility.rules[${ruleIndex}].optionKeys`,
                                  []
                                );
                                if (key === undefined) {
                                  if (formikValue.rules.length > 1) {
                                    arrayHelpers.remove(ruleIndex);
                                  }
                                }
                              }}
                            />
                            {!isLastRule ? (
                              <Box width="8rem" alignSelf="center">
                                <SegmentedControl
                                  options={[
                                    { label: "And", value: "and" },
                                    { label: "Or", value: "or" },
                                  ]}
                                  value={formikValue.logicalOperator}
                                  onChange={async (value) => {
                                    if (
                                      !(await LiveEditConfirmation.confirmQuestionVisibility())
                                    ) {
                                      return;
                                    }
                                    await formik.setFieldValue(
                                      "questionVisibility.logicalOperator",
                                      value
                                    );
                                  }}
                                />
                              </Box>
                            ) : null}
                          </>
                        )}
                      </RemoteDataView>
                    </Flex>
                  );
                })}
              </Flex>
              {canAddQuestion ? (
                <Button
                  variant="outline"
                  size="xs"
                  colorScheme="gray"
                  alignSelf="flex-start"
                  leftIcon={<RiAddCircleLine />}
                  onClick={async () => {
                    if (
                      !(await LiveEditConfirmation.confirmQuestionVisibility())
                    ) {
                      return;
                    }
                    arrayHelpers.push(NewRule);
                  }}
                >
                  Add question
                </Button>
              ) : null}
            </Flex>
          );
        }}
      </FieldArray>
    </Flex>
  );
};

type RuleProps = {
  sectionType: AF.V3.SectionType;
  otherQuestions: readonly AF.V3.Question[];
  schools: SchoolItem[];
  ruleIndex: number;
  onQuestionKeyChange: (key: string | undefined) => Promise<void>;
};
const Rule: React.FC<RuleProps> = ({
  sectionType,
  otherQuestions,
  schools,
  ruleIndex,
  onQuestionKeyChange,
}) => {
  const formik = useFormikContext<FormikType>();
  const formikValue = formik.values.questionVisibility;
  const LiveEditConfirmation = useLiveEditConfirmation();

  if (formikValue.switch === "disabled") {
    return null;
  }

  const questions = toQuestionItems(sectionType, otherQuestions);

  const rule = formikValue.rules[ruleIndex] ?? NewRule;
  const selectedKey = rule.questionKey;
  const selectedQuestion =
    otherQuestions.find((q) => q.id === selectedKey) ?? null;
  const missingQuestion =
    selectedKey !== undefined &&
    selectedKey !== "" &&
    selectedKey !== SchoolSelectionItem.key &&
    selectedQuestion === null;

  const options = toAnswerOptions(
    sectionType,
    selectedKey,
    otherQuestions,
    schools
  );

  const selectedAnswers = rule.optionKeys.flatMap((optionKey) => {
    const option = options?.find((o) => o.id === optionKey);
    if (option === undefined) {
      return [{ id: optionKey, label: optionKey }];
    }
    return [option];
  });

  const missingAnswers =
    rule.optionKeys.length > 0 && selectedAnswers.length === 0;

  const selectedItem =
    questions.find((item) => item.key === selectedKey) ??
    handleKeyNotFound(selectedKey);

  const optionKeysName = `questionVisibility.rules[${ruleIndex}].optionKeys`;
  const optionKeysMeta = formik.getFieldMeta(optionKeysName);

  const questionKeyName = `questionVisibility.rules[${ruleIndex}].questionKey`;
  const questionKeyMeta = formik.getFieldMeta(questionKeyName);

  return (
    <Flex direction="column" gap="2">
      <SearchSelect
        invalidSelectionErrorMessage={INVALID_SCHOOL_SELECTION_KEY}
        placeholder={
          sectionType === AF.V3.SectionTypes.PreRankingSection
            ? "Search for a question"
            : "Search for a question or school selection"
        }
        label={<VisuallyHidden>Question</VisuallyHidden>}
        items={questions}
        onChange={async (item: Item | null) => {
          if (!(await LiveEditConfirmation.confirmQuestionVisibility())) {
            return;
          }

          await onQuestionKeyChange(item?.key ?? "");
        }}
        selectedItem={selectedItem}
        error={
          missingQuestion
            ? "Revealing question was removed or changed, pick a different question to conditionally reveal this question."
            : // Show error when the field is touched, or when user submit the form
            (questionKeyMeta.touched || formik.submitCount > 0) &&
              questionKeyMeta.error
            ? questionKeyMeta.error
            : undefined
        }
      />
      {options !== null ? (
        <MultiSelectInput<Option>
          invalidSelectionErrorMessage={INVALID_ANSWERS}
          validateSelectedItems
          label={<VisuallyHidden>Answers</VisuallyHidden>}
          options={options}
          size="md"
          placeholder={
            selectedKey === SchoolSelectionItem.key
              ? "Please select one of multiple schools"
              : "Please select one or multiple answers"
          }
          initialValues={selectedAnswers}
          onChange={async (items) => {
            if (!(await LiveEditConfirmation.confirmQuestionVisibility())) {
              return;
            }
            await formik.setFieldValue(
              optionKeysName,
              items.map((i) => i.id)
            );
          }}
          renderDropdownItem={function (
            item: Option,
            keyword: string
          ): React.ReactElement {
            return (
              <HighlightedText text={item.label} keyword={keyword} invert />
            );
          }}
          error={
            missingAnswers
              ? "Revealing answer was removed or changed, pick a different answer to conditionally reveal this question."
              : // Show error when the field is touched, or when user submit the form
              (optionKeysMeta.touched || formik.submitCount > 0) &&
                optionKeysMeta.error
              ? optionKeysMeta.error
              : undefined
          }
        />
      ) : null}
    </Flex>
  );
};

type SwitchState = "checked" | "disabled" | undefined;
type SwitchProps = Props & {
  questionId: string;
};
export const QuestionVisibilitySwitch: React.FC<SwitchProps> = (props) => {
  return (
    <WithFeatureFlags featureFlags={["question-visibility-admin"]}>
      <QuestionVisibilitySwitchInternal {...props} />
    </WithFeatureFlags>
  );
};

const QuestionVisibilitySwitchInternal: React.FC<SwitchProps> = ({
  questionId,
  sectionType,
  otherQuestions,
  schools,
}) => {
  const fieldName = `questionVisibility.switch`;
  const id = `${fieldName}.${questionId}`;
  const LiveEditConfirmation = useLiveEditConfirmation();
  return (
    <FormikSwitchableContent<SwitchState>
      id={id}
      name={fieldName}
      label="Conditional reveal"
      showContent={(value) => value === "checked"}
      checked={["checked", ""]}
      unchecked={["disabled", ""]}
      beforeChange={async () => {
        return await LiveEditConfirmation.confirmQuestionVisibility();
      }}
    >
      <QuestionVisibility
        sectionType={sectionType}
        otherQuestions={otherQuestions}
        schools={schools}
      />
    </FormikSwitchableContent>
  );
};

type RelatedProps = {
  questionId: string;
  answerId: string;
  otherQuestions: readonly AF.V3.Question[];
  children?: (relatedQuestions: React.ReactNode) => React.ReactNode;
};
export const RelatedQuestionVisibility: React.FC<RelatedProps> = (props) => {
  return (
    <WithFeatureFlags featureFlags={["question-visibility-admin"]}>
      {RelatedQuestionVisibilityInternal(props)}
    </WithFeatureFlags>
  );
};
const RelatedQuestionVisibilityInternal: React.FC<RelatedProps> = ({
  questionId,
  answerId,
  otherQuestions,
  children,
}) => {
  const relatedQuestions = findRelatedQuestionVisibilityByAnswerId(
    otherQuestions,
    questionId,
    answerId
  );

  if (relatedQuestions.length === 0) {
    return null;
  }

  const relatedQuestionsElement = (
    <>
      {relatedQuestions.map((q, index) => {
        const isLast = relatedQuestions.length - 1 === index;
        let conjunction: React.ReactNode = null;
        if (index > 0) {
          conjunction = isLast ? ", and " : ", ";
        }
        return (
          <React.Fragment key={q.id}>
            {conjunction}
            <Link
              color="primary.500"
              textDecoration="underline"
              onClick={() => {
                document
                  .getElementById(q.id)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              {q.question}
            </Link>
          </React.Fragment>
        );
      })}
    </>
  );

  if (children !== undefined) {
    return children(relatedQuestionsElement);
  }

  return relatedQuestionsElement;
};

type Option = {
  id: string;
  label: string;
};

function handleKeyNotFound(selectedKey: string | undefined): Item | null {
  if (!isNonEmptyString(selectedKey)) {
    return null;
  }

  if (selectedKey === SchoolSelectionItem.key) {
    return SchoolSelectionItem;
  }

  return {
    key: selectedKey,
    label: "Invalid question",
  };
}
